// src/pages/PMSConfigPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Error, Sync, Save } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';

interface PMSConfig {
  pmsEnabled: boolean;
  pmsApiUsername: string | null;
  pmsHotelId: string | null;
  pmsPmsUsername: string | null;
  pmsLastSyncAt: Date | null;
  pmsConnectionStatus: string;
}

const PMSConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [config, setConfig] = useState<PMSConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form fields
  const [pmsEnabled, setPmsEnabled] = useState(false);
  const [pmsApiUsername, setPmsApiUsername] = useState('');
  const [pmsApiPassword, setPmsApiPassword] = useState('');
  const [pmsHotelId, setPmsHotelId] = useState('');
  const [pmsPmsUsername, setPmsPmsUsername] = useState('');
  const [pmsPmsPassword, setPmsPmsPassword] = useState('');
  
  const [showApiPassword, setShowApiPassword] = useState(false);
  const [showPmsPassword, setShowPmsPassword] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!user?.hotelId) return;

    try {
      setLoading(true);
      const response = await api.get(`/hotels/${user.hotelId}/pms-config`);
      setConfig(response.data);

      setPmsEnabled(response.data.pmsEnabled);
      setPmsApiUsername(response.data.pmsApiUsername || '');
      setPmsHotelId(response.data.pmsHotelId || '');
      setPmsPmsUsername(response.data.pmsPmsUsername || '');
      setHasUnsavedChanges(false);
    } catch (err: any) {
      setError(t('pmsConfig.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [user?.hotelId, t]);

  useEffect(() => {
    if (user?.hotelId) {
      fetchConfig();
    }
  }, [user?.hotelId, fetchConfig]);

  const handleSave = async () => {
    if (!user?.hotelId) return;

    setError('');
    setSuccess('');

    // Validate required fields when PMS is enabled
    if (pmsEnabled) {
      // Check if this is a new setup (no existing config) or an update
      const isNewSetup = !config?.pmsApiUsername && !config?.pmsHotelId && !config?.pmsPmsUsername;
      
      if (isNewSetup || pmsApiPassword || pmsPmsPassword) {
        // If it's a new setup, or if passwords are being changed, validate all fields
        if (!pmsApiUsername?.trim()) {
          setError(t('pmsConfig.apiUsernameRequired'));
          return;
        }
        if (!pmsHotelId?.trim()) {
          setError(t('pmsConfig.hotelIdRequired'));
          return;
        }
        if (!pmsPmsUsername?.trim()) {
          setError(t('pmsConfig.pmsUsernameRequired'));
          return;
        }
        if (isNewSetup) {
          if (!pmsApiPassword?.trim()) {
            setError(t('pmsConfig.apiPasswordRequired'));
            return;
          }
          if (!pmsPmsPassword?.trim()) {
            setError(t('pmsConfig.pmsPasswordRequired'));
            return;
          }
        }
      }
    }

    setSaving(true);

    try {
      const updateData: any = {
        pmsEnabled,
      };

      // Only include non-empty string fields
      if (pmsApiUsername && pmsApiUsername.trim()) {
        updateData.pmsApiUsername = pmsApiUsername.trim();
      }
      if (pmsHotelId && pmsHotelId.trim()) {
        updateData.pmsHotelId = pmsHotelId.trim();
      }
      if (pmsPmsUsername && pmsPmsUsername.trim()) {
        updateData.pmsPmsUsername = pmsPmsUsername.trim();
      }
      
      // Only include passwords if they're not empty
      if (pmsApiPassword && pmsApiPassword.trim()) {
        updateData.pmsApiPassword = pmsApiPassword.trim();
      }
      if (pmsPmsPassword && pmsPmsPassword.trim()) {
        updateData.pmsPmsPassword = pmsPmsPassword.trim();
      }

      const response = await api.put(`/hotels/${user.hotelId}/pms-config`, updateData);
      setConfig(response.data);
      setSuccess(t('pmsConfig.configurationSaved'));
      setHasUnsavedChanges(false);
      
      // Clear password fields after successful save
      setPmsApiPassword('');
      setPmsPmsPassword('');
      
      // Refresh the config to ensure we have the latest data
      await fetchConfig();
    } catch (err: any) {
      setError(err.response?.data?.message || t('pmsConfig.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!user?.hotelId) return;

    setError('');
    setTestResult(null);
    setTesting(true);

    try {
      const response = await api.post(`/hotels/${user.hotelId}/pms-config/test`);
      setTestResult(response.data);
      
      // Refresh config to get updated status
      await fetchConfig();
    } catch (err: any) {
      setError(err.response?.data?.message || t('pmsConfig.failedToTest'));
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    if (!user?.hotelId) return;

    setError('');
    setSuccess('');
    setSyncing(true);

    try {
      const response = await api.post(`/hotels/${user.hotelId}/pms-config/sync`);
      setSuccess(response.data.message || t('pmsConfig.syncSuccess'));
      await fetchConfig();
    } catch (err: any) {
      setError(err.response?.data?.message || t('pmsConfig.failedToSync'));
    } finally {
      setSyncing(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'connected':
        return <Chip icon={<CheckCircle />} label={t('pmsConfig.connected')} color="success" />;
      case 'error':
        return <Chip icon={<Error />} label={t('pmsConfig.error')} color="error" />;
      case 'disconnected':
      default:
        return <Chip label={t('pmsConfig.disconnected')} />;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('pmsConfig.title')}
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Configuration Form */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('pmsConfig.pmsCredentials')}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={pmsEnabled}
                  onChange={(e) => {
                    setPmsEnabled(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                  color="primary"
                />
              }
              label={t('pmsConfig.enablePmsIntegration')}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pmsConfig.apiUsername')}
                  value={pmsApiUsername}
                  onChange={(e) => {
                    setPmsApiUsername(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={!pmsEnabled}
                  helperText={t('pmsConfig.apiUsernameHelper')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pmsConfig.apiPassword')}
                  type={showApiPassword ? 'text' : 'password'}
                  value={pmsApiPassword}
                  onChange={(e) => {
                    setPmsApiPassword(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={!pmsEnabled}
                  helperText={
                    config?.pmsApiUsername && !pmsApiPassword
                      ? t('pmsConfig.passwordSaved')
                      : t('pmsConfig.enterApiPassword')
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowApiPassword(!showApiPassword)}
                          edge="end"
                        >
                          {showApiPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pmsConfig.hotelId')}
                  value={pmsHotelId}
                  onChange={(e) => {
                    setPmsHotelId(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={!pmsEnabled}
                  helperText={t('pmsConfig.hotelIdHelper')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pmsConfig.pmsUsername')}
                  value={pmsPmsUsername}
                  onChange={(e) => {
                    setPmsPmsUsername(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={!pmsEnabled}
                  helperText={t('pmsConfig.pmsUsernameHelper')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pmsConfig.pmsPassword')}
                  type={showPmsPassword ? 'text' : 'password'}
                  value={pmsPmsPassword}
                  onChange={(e) => {
                    setPmsPmsPassword(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={!pmsEnabled}
                  helperText={
                    config?.pmsPmsUsername && !pmsPmsPassword
                      ? t('pmsConfig.passwordSaved')
                      : t('pmsConfig.enterPmsPassword')
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPmsPassword(!showPmsPassword)}
                          edge="end"
                        >
                          {showPmsPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving || !pmsEnabled}
              >
                {saving ? <CircularProgress size={24} /> : t('pmsConfig.saveConfiguration')}
              </Button>
              {hasUnsavedChanges && (
                <Typography variant="body2" color="warning.main">
                  {t('pmsConfig.unsavedChanges')}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Status and Actions */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pmsConfig.connectionStatus')}
              </Typography>

              {config && (
                <>
                  <Box sx={{ mb: 2 }}>
                    {getStatusChip(config.pmsConnectionStatus)}
                  </Box>

                  {config.pmsLastSyncAt && (
                    <Typography variant="body2" color="text.secondary">
                      {t('pmsConfig.lastSync')} {new Date(config.pmsLastSyncAt).toLocaleString()}
                    </Typography>
                  )}
                </>
              )}

              {testResult && (
                <Alert 
                  severity={testResult.success ? 'success' : 'error'} 
                  sx={{ mt: 2 }}
                >
                  {testResult.message}
                </Alert>
              )}
            </CardContent>

            <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CheckCircle />}
                onClick={handleTestConnection}
                disabled={testing || !pmsEnabled || hasUnsavedChanges}
              >
                {testing ? <CircularProgress size={24} /> : t('pmsConfig.testConnection')}
              </Button>
              {hasUnsavedChanges && pmsEnabled && (
                <Typography variant="caption" color="warning.main" textAlign="center">
                  {t('pmsConfig.saveConfigurationFirst')}
                </Typography>
              )}

              <Button
                fullWidth
                variant="outlined"
                startIcon={<Sync />}
                onClick={handleSync}
                disabled={syncing || !pmsEnabled || config?.pmsConnectionStatus !== 'connected'}
              >
                {syncing ? <CircularProgress size={24} /> : t('pmsConfig.syncDataNow')}
              </Button>
            </CardActions>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pmsConfig.apiInformation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('pmsConfig.baseUrl')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pmsConfig.pmsIntegrationAllows')}
                <br />
                {t('pmsConfig.authenticateUsers')}
                <br />
                {t('pmsConfig.fetchReservations')}
                <br />
                {t('pmsConfig.manageTasks')}
                <br />
                {t('pmsConfig.trackActivities')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PMSConfigPage;

